/**
 * Dates des opérations (budget personnel et compte commun).
 *
 * Toutes les dates sont au format YYYY-MM-DD : la comparaison de chaînes suit
 * alors l'ordre chronologique.
 */

const pad = (n: number) => String(n).padStart(2, '0');

export function toISODate(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Premier et dernier jour d'un mois (bornes incluses). */
export function getMonthRange(month: number, year: number): { min: string; max: string } {
  const lastDay = new Date(year, month, 0).getDate();
  return { min: toISODate(year, month, 1), max: toISODate(year, month, lastDay) };
}

export function isInMonth(date: string, month: number, year: number): boolean {
  const { min, max } = getMonthRange(month, year);
  return date >= min && date <= max;
}

/**
 * Premier jour du plus ancien mois consultable avec l'offre, ou null si
 * l'historique est illimité (même règle que `isMonthRestricted`).
 */
export function getHistoryStartDate(historyMonths: number | null | undefined, now = new Date()): string | null {
  if (historyMonths == null || historyMonths < 0 || !Number.isFinite(historyMonths)) return null;
  const oldest = new Date(now.getFullYear(), now.getMonth() - (historyMonths - 1), 1);
  return toISODate(oldest.getFullYear(), oldest.getMonth() + 1, 1);
}

export type MinDateReason = 'month' | 'accountStart' | 'history';

export interface OperationDateBounds {
  minDate?: string;
  maxDate?: string;
  /** Contrainte qui fixe minDate, pour choisir le message d'erreur. */
  minReason?: MinDateReason;
}

/**
 * Bornes du champ Date d'une opération affichée dans le mois `month`/`year`.
 *
 * Une opération verrouillée sur son mois (`lockedToMonth`) reste entre le 1er
 * et le dernier jour du mois affiché ; les autres peuvent changer de mois, dans
 * la limite de la date de début du compte et de l'historique de l'offre.
 */
export function getOperationDateBounds(params: {
  month: number;
  year: number;
  lockedToMonth: boolean;
  accountStartDate?: string | null;
  historyStartDate?: string | null;
}): OperationDateBounds {
  const { month, year, lockedToMonth, accountStartDate, historyStartDate } = params;
  const range = getMonthRange(month, year);

  const candidates: Array<{ date: string; reason: MinDateReason }> = [];
  if (lockedToMonth) candidates.push({ date: range.min, reason: 'month' });
  if (accountStartDate) candidates.push({ date: accountStartDate, reason: 'accountStart' });
  if (historyStartDate) candidates.push({ date: historyStartDate, reason: 'history' });

  // La contrainte la plus récente l'emporte ; à égalité, la première citée.
  const min = candidates.reduce<(typeof candidates)[number] | undefined>(
    (best, c) => (!best || c.date > best.date ? c : best),
    undefined
  );

  return {
    minDate: min?.date,
    minReason: min?.reason,
    maxDate: lockedToMonth ? range.max : undefined,
  };
}

const toUTCNoon = (date: string) => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
};

/** « juillet 2026 » */
export function formatMonthLabel(date: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(toUTCNoon(date));
}

/** « mardi 14 juillet 2026 » */
export function formatLongDate(date: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  }).format(toUTCNoon(date));
}

/** « 14/07/2026 » */
export function formatShortDate(date: string): string {
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}`;
}
